import TitrePage from "../../components/charte/TitrePage";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createAdmin, getComptesClients, getComptesSpotters, getStatsComptes, listerAdmins,
  majAdmin, reactiverAdmin, revoquerAdmin, supprimerAdmin,
} from "../../utilities/adminservice";
import { blockUser, messageServeur, supprimerCompte, unblockUser } from "../../utilities/userService";
import { sujetCourant } from "../../api/role";
import { loaderOff, loaderOn, logout, openSnackbar } from "../../redux/actions";
import { emailRegex } from "../../CustomRegex/CustomRegex";
import store from "../../redux/store";
import { AIDE_REGLAGES } from "../../aide/textesAide";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: "#1F1E1B",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

const FIXED_ROLES = ["SUPER_ADMIN", "ADMIN", "VIEWER"] as const;

const roleColors: Record<string, { bg: string; color: string }> = {
  SUPER_ADMIN: { bg: "rgba(162,58,13,0.12)", color: "#A23A0D" },
  ADMIN:       { bg: "rgba(31,30,27,0.12)", color: "#1F1E1B" },
  VIEWER:      { bg: "rgba(87,83,74,0.14)", color: "#57534A" },
};

function KpiCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <Box sx={{ background: C.card, border: `1px solid ${C.borderAccent}`, borderRadius: "8px", padding: "20px 24px", flex: 1 }}>
      <Typography sx={{ color: C.textMuted, fontSize: "13px", marginBottom: "8px" }}>{label}</Typography>
      <Typography sx={{ color: color ?? C.accentLight, fontSize: "28px", fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}

function StatusChip({ blocked }: { blocked?: boolean }) {
  return (
    <Chip
      label={blocked ? "Bloqué" : "Actif"}
      size="small"
      sx={{
        background: blocked ? "rgba(162,58,13,0.12)" : "rgba(53,106,15,0.12)",
        color: blocked ? C.error : C.success,
        fontSize: "12px",
        fontWeight: 600,
      }}
    />
  );
}

function AdminAvatar({ name }: { name: string }) {
  const initials = name.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase();
  return (
    <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: C.accentMuted, border: `1px solid ${C.borderAccent}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.accentLight, fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
      {initials || "?"}
    </Box>
  );
}

function formatDate(v: any) {
  if (!v) return "—";
  // /sa renvoie un Timestamp SQL (« 2026-10-08 12:00:00.0 ») : séparateur ISO.
  const d = new Date(String(v).replace(" ", "T"));
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const initialForm = {
  first_name: { value: "", error: false, errorMessage: "Prénom requis" },
  last_name:  { value: "", error: false, errorMessage: "Nom requis" },
  email:      { value: "", error: false, errorMessage: "Email invalide" },
  domain:     { value: "", error: false, errorMessage: "" },
  role:       { value: "", error: false, errorMessage: "Rôle requis" },
};

export default function SuperAdmin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((s: any) => s.auth.isLoggedIn);
  const isTokenExpired = useSelector((s: any) => s.auth.isTokenExpired);


  const [activeTab, setActiveTab]           = useState(0);
  const [clients, setClients]               = useState<any[]>([]);
  const [collecteurs, setCollecteurs]       = useState<any[]>([]);
  const [admins, setAdmins]                 = useState<any[]>([]);
  // Compteurs calculés par le serveur (/sa/stats), sur toute la table des comptes.
  const [stats, setStats]                   = useState<any>(null);

  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [form, setForm]                     = useState(initialForm);
  const [editingAdminId, setEditingAdminId] = useState<number | null>(null);

  // actif absent (comptes historiques) = accès ouvert, comme côté serveur.
  const estActif       = (a: any) => a.actif !== false;
  const activeAdmins   = admins.filter(estActif).length;
  const inactiveAdmins = admins.length - activeAdmins;
  // Personne ne révoque, ne supprime ni ne change le rôle de son propre compte.
  const monEmail       = (sujetCourant() ?? "").toLowerCase();
  const estMoi         = (a: any) => (a.email ?? "").toLowerCase() === monEmail;

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchUsers = async () => {
    const [sp, cl, st] = await Promise.allSettled([getComptesSpotters(), getComptesClients(), getStatsComptes()]);
    setCollecteurs(sp.status === "fulfilled" ? sp.value : []);
    setClients(cl.status === "fulfilled" ? cl.value : []);
    setStats(st.status === "fulfilled" ? st.value : null);
    if (sp.status === "rejected" || cl.status === "rejected" || st.status === "rejected") {
      dispatch(openSnackbar("Erreur lors du chargement des comptes", "red"));
    }
  };

  const fetchAdmins = async () => {
    try {
      setAdmins(await listerAdmins());
    } catch {
      dispatch(openSnackbar("Erreur lors du chargement des admins", "red"));
      setAdmins([]);
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem("jwtToken")) return;
    dispatch(loaderOn());
    Promise.allSettled([fetchUsers(), fetchAdmins()]).then(() => dispatch(loaderOff()));
  }, []);

  // ── Actions sur les comptes Spotters et clients ──────────────────────────────────────────────────

  const handleBlock = async (item: any) => {
    const motif = (window.prompt(`Motif du blocage de ${item.email ?? item.id} (obligatoire, conservé au journal) :`) || "").trim();
    if (!motif) return;
    dispatch(loaderOn());
    try {
      await blockUser(item.id, motif);
      dispatch(openSnackbar("Compte bloqué", "green"));
      fetchUsers();
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Erreur lors du blocage"), "red"));
    } finally { dispatch(loaderOff()); }
  };

  const handleUnblock = async (item: any) => {
    if (!window.confirm(`Débloquer ${item.email ?? item.id} ?`)) return;
    dispatch(loaderOn());
    try {
      await unblockUser(item.id);
      dispatch(openSnackbar("Compte débloqué", "green"));
      fetchUsers();
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Erreur lors du déblocage"), "red"));
    } finally { dispatch(loaderOff()); }
  };

  const handleDeleteUser = async (item: any) => {
    if (!window.confirm(`Supprimer définitivement ${item.email ?? item.id} ? Cette action est irréversible.`)) return;
    dispatch(loaderOn());
    try {
      await supprimerCompte(item.id);
      dispatch(openSnackbar("Compte supprimé", "green"));
      fetchUsers();
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Erreur lors de la suppression"), "red"));
    } finally { dispatch(loaderOff()); }
  };

  // ── Admin actions ────────────────────────────────────────────────────────────

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    let error = false;
    if (name === "email") error = !emailRegex.test(value);
    else if (name === "first_name" || name === "last_name") error = value === "";
    setForm((prev) => ({ ...prev, [name]: { ...prev[name as keyof typeof initialForm], value, error } }));
  };

  const validateForm = (): boolean => {
    let hasError = false;
    const updated = { ...form };
    if (!updated.first_name.value) { updated.first_name = { ...updated.first_name, error: true }; hasError = true; }
    if (!updated.last_name.value)  { updated.last_name  = { ...updated.last_name,  error: true }; hasError = true; }
    if (!updated.email.value || !emailRegex.test(updated.email.value)) { updated.email = { ...updated.email, error: true }; hasError = true; }
    if (!updated.role.value) { updated.role = { ...updated.role, error: true }; hasError = true; }
    setForm(updated);
    return hasError;
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setForm(initialForm);
    setEditingAdminId(null);
  };

  const handleSubmit = async () => {
    if (validateForm()) return;
    // Rôle fixe envoyé par son nom (Q22). Le serveur génère le mot de passe
    // temporaire et l'envoie par email : aucun mot de passe ne part d'ici.
    const body = {
      first_name: form.first_name.value,
      last_name:  form.last_name.value,
      domain:     form.domain.value,
      role:       form.role.value,
    };
    dispatch(loaderOn());
    try {
      if (editingAdminId) {
        await majAdmin(editingAdminId, body);
        dispatch(openSnackbar("Admin modifié", "green"));
      } else {
        await createAdmin({ ...body, email: form.email.value.trim(), given_User_Name: form.email.value.trim() });
        dispatch(openSnackbar("Admin créé : le mot de passe temporaire lui a été envoyé par email", "green"));
      }
      closeDrawer();
      fetchAdmins();
    } catch (e: any) {
      const d = e?.response?.data;
      dispatch(openSnackbar(typeof d === "string" && /already exists/i.test(d)
        ? "Un admin existe déjà avec cet email"
        : e?.response?.status === 403 ? "Action réservée au super admin" : "Erreur lors de l'enregistrement", "red"));
    } finally { dispatch(loaderOff()); }
  };

  const openEditDrawer = (admin: any) => {
    const roleValue = getRoleName(admin) === "—" ? "" : getRoleName(admin);
    setForm({
      first_name: { value: admin.first_name ?? "", error: false, errorMessage: "Prénom requis" },
      last_name:  { value: admin.last_name  ?? "", error: false, errorMessage: "Nom requis" },
      email:      { value: admin.email      ?? "", error: false, errorMessage: "Email invalide" },
      domain:     { value: admin.domain     ?? "", error: false, errorMessage: "" },
      role:       { value: roleValue, error: false, errorMessage: "Rôle requis" },
    });
    setEditingAdminId(admin.id ?? null);
    setDrawerOpen(true);
  };

  const handleRevoke = async (admin: any) => {
    const actif = estActif(admin);
    if (!window.confirm(actif
      ? `Révoquer l'accès de ${admin.email} ? Il ne pourra plus se connecter.`
      : `Rétablir l'accès de ${admin.email} ?`)) return;
    dispatch(loaderOn());
    try {
      await (actif ? revoquerAdmin(admin.id) : reactiverAdmin(admin.id));
      dispatch(openSnackbar(actif ? "Accès révoqué" : "Accès rétabli", "green"));
      fetchAdmins();
    } catch {
      dispatch(openSnackbar("Erreur lors du changement d'accès", "red"));
    } finally { dispatch(loaderOff()); }
  };

  const handleDeleteAdmin = async (admin: any) => {
    if (!window.confirm(`Supprimer définitivement l'admin ${admin.email} ?`)) return;
    dispatch(loaderOn());
    try {
      await supprimerAdmin(admin.id);
      dispatch(openSnackbar("Admin supprimé", "green"));
      fetchAdmins();
    } catch {
      dispatch(openSnackbar("Erreur lors de la suppression", "red"));
    } finally { dispatch(loaderOff()); }
  };

  // ── Shared sx ────────────────────────────────────────────────────────────────

  const cellSx = { borderBottom: `1px solid ${C.border}`, color: C.textSub, fontSize: "13px" };
  const headSx = { color: C.textMuted, fontSize: "12px", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.06em", borderBottom: `1px solid ${C.border}`, background: C.card };
  const btnSx = (color?: string) => ({
    borderColor: color ? `${color}55` : C.borderAccent,
    color: color ?? C.accentLight,
    borderRadius: "6px",
    textTransform: "none" as const,
    fontSize: "12px",
    padding: "2px 10px",
    "&:hover": { background: color ? `${color}15` : C.accentMuted },
  });
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: "rgba(31,30,27,0.04)", borderRadius: "8px", color: C.text,
      "& fieldset": { borderColor: C.border },
      "&:hover fieldset": { borderColor: C.borderAccent },
      "&.Mui-focused fieldset": { borderColor: C.accentLight },
    },
    "& .MuiInputLabel-root": { color: C.textMuted },
    "& .MuiInputLabel-root.Mui-focused": { color: C.accentLight },
  };

  // admin.role fait autorité côté serveur ; role_type en est le reflet.
  function getRoleName(admin: any): string {
    return admin.role ?? admin.role_type?.role_name ?? "—";
  }

  const getFullName = (admin: any) =>
    [admin.first_name, admin.last_name].filter(Boolean).join(" ") || admin.email || "—";

  return (
    <Box sx={{ minHeight: "100vh", background: C.bg, padding: "32px", color: C.text }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <Box>
          <TitrePage domaine="reglages" aide={AIDE_REGLAGES.equipe}>Équipe et comptes</TitrePage>
          <Typography sx={{ fontSize: "14px", color: C.textMuted, marginTop: "4px" }}>
            Les admins du back-office, les comptes Spotters et clients.
          </Typography>
        </Box>
        {activeTab === 2 && (
          <Button
            variant="contained"
            onClick={() => { setEditingAdminId(null); setForm(initialForm); setDrawerOpen(true); }}
            sx={{ background: C.accent, borderRadius: "8px", textTransform: "none", fontWeight: 600, padding: "10px 20px", "&:hover": { background: C.accentLight } }}
          >
            + Ajouter un admin
          </Button>
        )}
      </Box>

      {/* KPI Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "32px" }}>
        <KpiCard label="Spotters"          value={stats?.totalCollecteurs ?? "—"} />
        <KpiCard label="Clients"           value={stats?.totalClients ?? "—"} />
        <KpiCard label="Admins"            value={stats?.totalAdmins ?? admins.length} />
        <KpiCard label="Comptes créés ce mois (Spotters et clients)" value={stats?.nouveauxCeMois ?? "—"} color={C.success} />
      </Box>

      {/* Tabs */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: `1px solid ${C.border}`,
            "& .MuiTab-root": { color: C.textMuted, textTransform: "none", fontWeight: 500, fontSize: "14px" },
            "& .Mui-selected": { color: C.accentLight },
            "& .MuiTabs-indicator": { backgroundColor: C.accentLight },
            px: 2,
          }}
        >
          <Tab label={`Spotters (${collecteurs.length})`} />
          <Tab label={`Clients (${clients.length})`} />
          <Tab label={`Admins / Équipe (${admins.length})`} />
        </Tabs>

        {/* ── TAB SPOTTERS ──────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Email", "Nom", "Niveau", "Tips disponibles", "Date inscription", "Statut", "Actions"].map((h) => (
                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {collecteurs.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ color: C.textMuted, textAlign: "center", padding: "40px", borderBottom: "none" }}>Aucun Spotter</TableCell></TableRow>
                )}
                {collecteurs.map((c: any, i: number) => (
                  <TableRow key={c.id ?? i} sx={{ "&:hover": { background: "rgba(31,30,27,0.02)" } }}>
                    <TableCell sx={cellSx}>{c.email ?? "—"}</TableCell>
                    <TableCell sx={{ ...cellSx, color: C.text, fontWeight: 500 }}>{[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip label={c.currentLevel ?? "—"} size="small" sx={{ background: C.accentMuted, color: C.accentLight, fontSize: "12px" }} />
                    </TableCell>
                    <TableCell sx={cellSx}>{c.tipsDisponibles ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>{formatDate(c.createdAt)}</TableCell>
                    <TableCell sx={cellSx}><StatusChip blocked={c.accountStatus !== "active"} /></TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: "flex", gap: "6px" }}>
                        {c.accountStatus === "active"
                          ? <Button size="small" variant="outlined" onClick={() => handleBlock(c)} sx={btnSx(C.warning)}>Bloquer</Button>
                          : <Button size="small" variant="outlined" onClick={() => handleUnblock(c)} sx={btnSx(C.success)}>Débloquer</Button>
                        }
                        <Button size="small" variant="outlined" onClick={() => handleDeleteUser(c)} sx={btnSx(C.error)}>Supprimer</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── TAB CLIENTS ──────────────────────────────────────────────────── */}
        {activeTab === 1 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Email", "Nom", "Date inscription", "Statut", "Actions"].map((h) => (
                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length === 0 && (
                  <TableRow><TableCell colSpan={5} sx={{ color: C.textMuted, textAlign: "center", padding: "40px", borderBottom: "none" }}>Aucun client</TableCell></TableRow>
                )}
                {clients.map((c: any, i: number) => (
                  <TableRow key={c.id ?? i} sx={{ "&:hover": { background: "rgba(31,30,27,0.02)" } }}>
                    <TableCell sx={cellSx}>{c.email ?? "—"}</TableCell>
                    <TableCell sx={{ ...cellSx, color: C.text, fontWeight: 500 }}>{[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell sx={cellSx}>{formatDate(c.createdAt)}</TableCell>
                    <TableCell sx={cellSx}><StatusChip blocked={c.accountStatus !== "active"} /></TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: "flex", gap: "6px" }}>
                        {c.accountStatus === "active"
                          ? <Button size="small" variant="outlined" onClick={() => handleBlock(c)} sx={btnSx(C.warning)}>Bloquer</Button>
                          : <Button size="small" variant="outlined" onClick={() => handleUnblock(c)} sx={btnSx(C.success)}>Débloquer</Button>
                        }
                        <Button size="small" variant="outlined" onClick={() => handleDeleteUser(c)} sx={btnSx(C.error)}>Supprimer</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── TAB ADMINS / ÉQUIPE ──────────────────────────────────────────── */}
        {activeTab === 2 && (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
              <KpiCard label="Total admins"   value={admins.length} />
              <KpiCard label="Accès actifs"   value={activeAdmins}  color={C.success} />
              <KpiCard label="Accès révoqués" value={inactiveAdmins} color={inactiveAdmins > 0 ? C.error : C.textMuted} />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["", "Nom / Email", "Rôle", "Statut", "Actions"].map((h) => (
                      <TableCell key={h} sx={headSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: C.textMuted, textAlign: "center", padding: "40px", borderBottom: "none" }}>
                        Aucun administrateur trouvé
                      </TableCell>
                    </TableRow>
                  )}
                  {admins.map((admin: any, idx: number) => {
                    const fullName  = getFullName(admin);
                    const roleName  = getRoleName(admin);
                    const roleStyle = roleColors[roleName] ?? { bg: C.accentMuted, color: C.accentLight };
                    return (
                      <TableRow key={admin.id ?? idx} sx={{ "&:hover": { background: "rgba(31,30,27,0.02)" } }}>
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}`, width: 48 }}>
                          <AdminAvatar name={fullName} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                          <Typography sx={{ color: C.text, fontSize: "14px", fontWeight: 600 }}>{fullName}</Typography>
                          <Typography sx={{ color: C.textMuted, fontSize: "12px" }}>{admin.email ?? "—"}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                          <Chip label={roleName} size="small" sx={{ background: roleStyle.bg, color: roleStyle.color, border: `1px solid ${roleStyle.color}30`, fontSize: "12px", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                          <Chip
                            label={estActif(admin) ? "Actif" : "Révoqué"}
                            size="small"
                            sx={{ background: estActif(admin) ? "rgba(53,106,15,0.12)" : "rgba(162,58,13,0.12)", color: estActif(admin) ? C.success : C.error, fontSize: "12px", fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                          <Box sx={{ display: "flex", gap: "6px" }}>
                            <Button size="small" variant="outlined" onClick={() => openEditDrawer(admin)} sx={btnSx()}>Modifier</Button>
                            {estMoi(admin) ? (
                              <Chip label="Vous" size="small" sx={{ background: C.accentMuted, color: C.accentLight, fontSize: "12px" }} />
                            ) : (<>
                              <Button size="small" variant="outlined" onClick={() => handleRevoke(admin)} sx={btnSx(estActif(admin) ? C.warning : C.success)}>
                                {estActif(admin) ? "Révoquer" : "Réactiver"}
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => handleDeleteAdmin(admin)} sx={btnSx(C.error)}>Supprimer</Button>
                            </>)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      {/* Admin Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: 440, background: C.card, borderLeft: `1px solid ${C.borderAccent}`, padding: "32px 28px", display: "flex", flexDirection: "column", gap: "24px" } }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: C.text }}>
          {editingAdminId ? "Modifier un admin" : "Ajouter un admin"}
        </Typography>

        <Box>
          <Typography sx={{ color: C.textSub, fontSize: "13px", marginBottom: "6px" }}>Prénom *</Typography>
          <TextField fullWidth name="first_name" value={form.first_name.value} onChange={handleFormChange} error={form.first_name.error} helperText={form.first_name.error ? form.first_name.errorMessage : ""} sx={inputSx} />
        </Box>

        <Box>
          <Typography sx={{ color: C.textSub, fontSize: "13px", marginBottom: "6px" }}>Nom *</Typography>
          <TextField fullWidth name="last_name" value={form.last_name.value} onChange={handleFormChange} error={form.last_name.error} helperText={form.last_name.error ? form.last_name.errorMessage : ""} sx={inputSx} />
        </Box>

        <Box>
          <Typography sx={{ color: C.textSub, fontSize: "13px", marginBottom: "6px" }}>Email *</Typography>
          <TextField fullWidth name="email" type="email" value={form.email.value} onChange={handleFormChange} disabled={!!editingAdminId} error={form.email.error} helperText={form.email.error ? form.email.errorMessage : editingAdminId ? "L'email ne se modifie pas" : ""} sx={inputSx} />
        </Box>

        <Box>
          <Typography sx={{ color: C.textSub, fontSize: "13px", marginBottom: "6px" }}>Domaine</Typography>
          <TextField fullWidth name="domain" placeholder="tribe.sn" value={form.domain.value} onChange={handleFormChange} sx={inputSx} />
        </Box>

        <Box>
          <Typography sx={{ color: C.textSub, fontSize: "13px", marginBottom: "6px" }}>Rôle *</Typography>
          <FormControl fullWidth error={form.role.error}>
            <Select
              name="role"
              value={form.role.value}
              onChange={(e: any) => setForm((prev) => ({ ...prev, role: { ...prev.role, value: e.target.value, error: false } }))}
              disabled={!!editingAdminId && admins.some((a) => a.id === editingAdminId && estMoi(a))}
              displayEmpty
              sx={{ background: "rgba(31,30,27,0.04)", borderRadius: "8px", color: form.role.value ? C.text : C.textMuted, "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.borderAccent }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: C.accentLight }, "& .MuiSvgIcon-root": { color: C.textMuted } }}
            >
              <MenuItem value="" disabled sx={{ color: C.textMuted }}>Sélectionner un rôle</MenuItem>
              {FIXED_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
            {form.role.error && <Typography sx={{ color: C.error, fontSize: "12px", marginTop: "4px" }}>{form.role.errorMessage}</Typography>}
          </FormControl>
        </Box>

        {!editingAdminId && (
          <Typography sx={{ color: C.textMuted, fontSize: "12px" }}>
            Un mot de passe temporaire est généré par le serveur et envoyé par email au nouvel admin.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: "12px", marginTop: "auto" }}>
          <Button fullWidth variant="outlined" onClick={closeDrawer}
            sx={{ borderColor: C.border, color: C.textSub, borderRadius: "8px", textTransform: "none", "&:hover": { borderColor: C.borderAccent, color: C.text } }}>
            Annuler
          </Button>
          <Button fullWidth variant="contained" onClick={handleSubmit}
            sx={{ background: C.accent, borderRadius: "8px", textTransform: "none", fontWeight: 600, "&:hover": { background: C.accentLight } }}>
            {editingAdminId ? "Enregistrer" : "Créer"}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
